import { useState } from "react";
import { Switch, IconButton, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ComputerCronJobModel from "../models/computerCronJobModel";
import moment from "moment";

const BRAND = "#a388f3";
const BRAND_HOVER = "#8b6fe0";

const statusDotStyle = (color) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: color,
  flexShrink: 0,
});

export default function ScheduledTaskRow({ job, uid, onToggle, onDelete, onEdit, onRunNow }) {
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  const status = ComputerCronJobModel.getJobStatus(job);
  const scheduleText = ComputerCronJobModel.describeSchedule(job.schedule);

  const nextRun = job.state?.nextRunAt;
  let nextRunText = "\u2014";
  if (nextRun && job.enabled) {
    const ts = nextRun._seconds ? nextRun._seconds * 1000 : nextRun.seconds ? nextRun.seconds * 1000 : new Date(nextRun).getTime();
    if (!isNaN(ts)) {
      nextRunText = moment(ts).fromNow();
    }
  }

  const handleToggle = async () => {
    setToggling(true);
    await onToggle(job.id);
    setToggling(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3000);
      return;
    }
    setDeleting(true);
    await onDelete(job.id);
    setDeleting(false);
    setConfirmDelete(false);
  };

  const handleRunNow = async () => {
    setRunningNow(true);
    await onRunNow(job.id);
    setRunningNow(false);
  };

  return (
    <div style={{
      padding: "10px 12px",
      borderBottom: "1px solid var(--border-subtle)",
      opacity: deleting ? 0.5 : 1,
      transition: "opacity 0.2s",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Tooltip title={status.label} arrow>
          <div style={statusDotStyle(status.color)} />
        </Tooltip>
        <span style={{
          fontWeight: 600,
          fontSize: 13,
          color: "var(--text-strong)",
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
          {job.name}
        </span>
        <Switch
          size="small"
          checked={!!job.enabled}
          onChange={handleToggle}
          disabled={toggling}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: BRAND },
          }}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--text-secondary)", marginLeft: 16 }}>
        <span>{scheduleText}</span>
        <span style={{ color: "var(--border-strong)" }}>|</span>
        <span>Next: {nextRunText}</span>
      </div>

      {job.state?.lastStatus === "error" && job.state?.lastError && (
        <div style={{ fontSize: 11, color: "var(--danger-fg)", marginTop: 4, marginLeft: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {job.state.lastError}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 2, marginTop: 2 }}>
        {job.enabled && (
          <Tooltip title={runningNow ? "Queued \u2014 runs within 2 min" : "Run now"} arrow>
            <span>
              <IconButton
                size="small"
                onClick={handleRunNow}
                disabled={runningNow}
                sx={{ color: runningNow ? BRAND : "var(--text-muted)", "&:hover": { color: BRAND } }}
              >
                <PlayArrowIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Edit" arrow>
          <IconButton size="small" onClick={() => onEdit(job)} sx={{ color: "var(--text-muted)", "&:hover": { color: BRAND } }}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={confirmDelete ? "Click again to confirm" : "Delete"} arrow>
          <IconButton size="small" onClick={handleDelete} disabled={deleting} sx={{ color: confirmDelete ? "var(--danger-fg)" : "var(--text-muted)", "&:hover": { color: "var(--danger-fg)" } }}>
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}
